import AllInboxIcon from "@mui/icons-material/AllInbox";
import {
  Box,
  Button,
  Link as Linked,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextareaAutosize,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {Controller, SubmitHandler, useForm} from "react-hook-form";
import {useQueryClient} from "react-query";
import {useNavigate, useParams} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {levelSelector, tokenSelector, userState} from "../../Recoil/Atom/authState";
import * as suggestionApi from "../../api/suggestionApi/suggestionApi";
import MWCaption from "../../components/MWCaption";
import MWProgress from "../../components/MWProgress";
import {useGetItemQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {ResponseItem, ResponseRemove} from "../../models/clientModels/interfaces/Response";
import * as Params from "../../models/clientModels/interfaces/apiParams";
import {EQSuggestion} from "../../models/serverModels/report/EQSuggestion";
import {FormContents} from "../../styledComponents/StyledCreateForm";
import {StyledDetailContainer, StyledDetailTable} from "../../styledComponents/StyledDetail";

const SuggestionRead = () => {
  let {id} = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const tokenState = useRecoilValue(tokenSelector);
  const userLevel = useRecoilValue(levelSelector);
  const user = useRecoilValue(userState);
  const setAlertState = useSetRecoilState(alertStateAtom);

  const isMobile = useMediaQuery("(min-width: 960px)");

  const {
    data: suggestion,
    isLoading,
    isFetching,
  } = useGetItemQuery<ResponseItem<EQSuggestion>>({
    key: ["ReadSuggestion", id],
    api: () => suggestionApi.read({id: parseInt(id!), token: tokenState}),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: suggestionApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("SuggestionAll");
        navigate(`/Suggestion/All`);
      },
    },
  });
  const {mutate: completeMutate} = useRemoveMutation<
    ResponseItem<EQSuggestion>,
    Params.Update<EQSuggestion>
  >({
    api: suggestionApi.complete,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("SuggestionAll");
        navigate(`/Suggestion/All`);
      },
    },
  });

  const {handleSubmit, control} = useForm<EQSuggestion>({
    defaultValues: suggestion?.item,
  });

  const onSubmit: SubmitHandler<EQSuggestion> = (data) => {
    completeMutate({
      id: parseInt(id!),
      token: tokenState,
      item: {...suggestion?.item, ...data},
    });
  };

  return (
    <StyledDetailContainer>
      <MWCaption title={"게시글 보기"} color="#01579B" icon={<AllInboxIcon />} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormContents container rowSpacing={4}>
          <StyledDetailTable>
            {isLoading && isFetching ? (
              <MWProgress />
            ) : (
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" width="10%">
                      제목
                    </TableCell>
                    <TableCell component="td" colSpan={3}>
                      {suggestion?.item?.title}
                    </TableCell>
                    <TableCell component="th" width="10%">
                      작성일자
                    </TableCell>
                    <TableCell component="td" width="23.33%">
                      {new Date(suggestion?.item?.entryDate!).getFullYear()}년
                      {new Date(suggestion?.item?.entryDate!).getMonth() + 1}월
                      {new Date(suggestion?.item?.entryDate!).getDate()}일
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th">유형</TableCell>
                    <TableCell component="td">{suggestion?.item?.type}</TableCell>
                    <TableCell component="th" width="10%">
                      URL
                    </TableCell>
                    <TableCell component="td" width="23.33%">
                      {suggestion?.item?.url}
                    </TableCell>
                    <TableCell component="th">첨부파일</TableCell>
                    <TableCell component="td">
                      <Linked
                        href={`/API/SuggestionAttachment?token=${tokenState}&id=${suggestion?.item?.uid}`}
                      >
                        {suggestion?.item?.otherFileName}
                      </Linked>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="td" colSpan={6}>
                      <Typography sx={{whiteSpace: "pre-line"}}>
                        {suggestion?.item?.report}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {suggestion?.item?.imageFileName && (
                    <TableRow>
                      <TableCell component="td" colSpan={6}>
                        <img
                          src={`/API/SuggestionImage?token=${tokenState}&id=${suggestion?.item?.uid}`}
                          alt="게시글 첨부 이미지"
                          style={{width: "500px", height: "auto", display: "block", margin: "auto"}}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell component="td" colSpan={6}>
                      <Controller
                        name="solution"
                        control={control}
                        render={({field: {onChange, value}}) => (
                          <TextareaAutosize
                            defaultValue={value}
                            aria-label="minimum height"
                            minRows={6}
                            onChange={onChange}
                            style={{width: "100%", padding: "8px", whiteSpace: "pre-line"}}
                          />
                        )}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </StyledDetailTable>
        </FormContents>
        <Box display="flex" justifyContent="center" margin="36px">
          <Button
            variant="contained"
            color="warning"
            size="small"
            onClick={() => navigate(-1)}
            sx={{marginRight: "8px"}}
          >
            뒤로가기
          </Button>

          <Button
            variant="contained"
            color="secondary"
            size="small"
            sx={{marginRight: "8px"}}
            onClick={() => {
              setAlertState({
                toggle: true,
                message: "등록된 게시글을 삭제하시겠습니까?",
                execution: () => removeMutate({token: tokenState, id: suggestion?.item?.uid!}),
              });
            }}
          >
            삭제
          </Button>

          <Button type="submit" variant="contained" color="success" size="small">
            수정
          </Button>
        </Box>
      </form>
    </StyledDetailContainer>
  );
};

export default SuggestionRead;
